import * as R from "remeda";

import { type TGlobalProduct, type TProduct, useAllProducts, useProductById, useProductsByIds } from "../../data";
import { globalProductName, packageNameWithSource, productName } from "../utils";
import { toGlobalCurveOption } from "./global-curve-options";
import { useGraphQLClient } from "@package/graphql/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useProductConfigsCache } from "../../data/cached/preload";
import { CURVE_MANAGEMENT_MAX_CELLS } from "./constants";
import {
  AllGlobalisedPackageIdsQuery,
  CurveCommodityGroupFragment,
  CurveCommodityGroupsQuery,
  CurveCommodityParentGroupFragment,
  CurveCommodityParentGroupsQuery,
  CurveEODProductsQuery,
  CurveGlobalDeleteMutation,
  CurveGlobalDependenciesQuery,
  CurveGlobalInsertMutation,
  CurvePackageFragment,
  CurvePackageQuery,
  CurveProductTypeFragment,
  CurveProductTypeQuery,
  CurveRegionsFragment,
  CurveRegionsQuery,
  CurveUOMFragment,
  CurveUOMQuery,
  CurveUpsertMutation,
  GlobalCurveMirrorsQuery,
  GlobalCurvesQuery,
  GlobalPackagesByIdsQuery,
  GlobalisedPackageFlagsQuery,
  InsertGlobalCurveSelfDepsMutation,
  RemoveGlobalCurvesMutation,
  UpsertGlobalCurvesMutation,
} from "./queries";
import { readFragment } from "../../graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigsQuery, GlobalProductInfoFragment, ProductInfoFragment } from "../../data/products/queries";
import { uppercaseFirstLetter } from "../catalogue/helpers";
import { GLOBALISED_NON_MANAGER_FOLDER_TOOLTIP } from "./globalisedFolders";
import { makeUomOptions } from "./uom";
import { buildCurveCommodityComboboxOptions } from "./form/commodityCombobox";
import { makeEodCurveOptions } from "./eod-options";
import { useCurveFormContext } from "./context";
import type { TCurve, TEntry, TSelectors } from "./types";
import { nanoid } from "nanoid";
import {
  formatFormulaOperators,
  getInputCurveSelectors,
  parseFormula,
  parseItems,
  replaceFormulaIds,
} from "./formula/helpers";
import type {
  Global_Product_Config_Insert_Input,
  Global_Product_Insert_Input,
  Product_Config_Insert_Input,
  Product_Insert_Input,
} from "../../__generated__/gql/graphql";
import { logger } from "@artis/logger";
import { timeout } from "@package/util/promise";
import { v4 as uuidv4 } from "uuid";
import { useCurrentUser, useUserHasRole, useUserSubscriptionTier } from "../../data/user";
import { useClaims, useUserId } from "../../context/auth/hooks";

export function useAllProductsOptions(props?: {
  disableListPermission?: boolean;
  disabledIds?: string[];
  filter?: Array<"live" | "snapshot" | "delta" | "all">;
}) {
  const { data, isFetching } = useAllProducts();
  const options = useProductOptions({ ...props, products: Object.values(data ?? {}) });
  return { data: options, fetching: isFetching };
}

// Pretty names for product types. Actual types come from the DB.
export const productTypes: Record<string, string> = {
  outright: "Outright",
  timespread: "Time Spread",
  crack: "Crack",
  arb: "Arb",
  diff: "Diff",
  box: "Box",
};

export const useProductOptions = (props?: {
  products: ReadonlyArray<TProduct>;
  disableListPermission?: boolean;
  disabledIds?: string[];
  filter?: Array<"live" | "snapshot" | "delta" | "all">;
}) => {
  const { disableListPermission, disabledIds: excludeIds } = props || {};
  const { liteUser, enhancedNoExchUser } = useUserSubscriptionTier();

  const curves = R.pipe(
    props?.products ?? [],
    R.groupBy((p) => p.packageByPackage.name),
    R.mapValues(R.piped(R.sortBy([R.prop("name"), "asc"]))),
    R.values(),
    R.flat(),
  );

  const { types, regions, commodities } = curves.reduce(
    (acc, curve) => {
      // Types
      const type = curve.product_type;
      if (type && productTypes[type] && !acc.types.find((t) => t.value === type)) {
        acc.types.push({ label: productTypes[type] || type, value: type });
      }

      // Regions
      const region = curve.geographicalRegionByGeographicalRegion?.geographical_region;
      if (region && !acc.regions.find((r) => r.value === region)) {
        acc.regions.push({ label: region, value: region });
      }

      // Commodities
      const commodity = curve.commodityGroupByCommodityGroup?.commodity_group;
      if (commodity && !acc.commodities.find((c) => c.value === commodity)) {
        acc.commodities.push({ label: commodity, value: commodity });
      }

      return acc;
    },
    {
      types: [] as { label: string; value: string }[],
      regions: [] as { label: string; value: string }[],
      commodities: [] as { label: string; value: string }[],
    },
  );

  const result = {
    curves:
      curves.map((p) => {
        const filter = props?.filter && !props.filter.includes(p.curve_type ?? "") && !props.filter.includes("all");

        const allowed = liteUser || enhancedNoExchUser ? p.packageByPackage?.sourceBySource?.exchange === null : true;

        const packageWithSource = packageNameWithSource({ packageByPackage: p.packageByPackage });
        const groupValue = packageWithSource || p.packageByPackage.name;

        const hasListPermission = p.packageByPackage.permissions?.[0]?.permission === "list";
        const disabled = (disableListPermission && hasListPermission) || excludeIds?.includes(p.id);

        let sourceType = p.packageByPackage?.sourceBySource?.source_type;
        const sourceName = p.packageByPackage?.sourceBySource?.name ?? "";
        if (!sourceType) {
          const hasExchange =
            p.packageByPackage?.sourceBySource?.exchange !== null &&
            p.packageByPackage?.sourceBySource?.exchange !== undefined;

          if (hasExchange) {
            sourceType = "exchange";
          } else if (sourceName.toLowerCase().includes("artis")) {
            sourceType = "organisation";
          } else {
            sourceType = "eod";
          }
        }

        return {
          label: productName(p),
          value: p.id,
          disabled,
          groupValue,
          groupLabel: packageWithSource,
          groupKey: String(p.package ?? p.packageByPackage?.name ?? p.id),
          metadata: p,
          hidden: filter || !allowed,
          shortName: p.name,
          longName: p.description !== p.name ? p.description : undefined,
          sortSourceType: sourceType,
          sortSourceName: sourceName,
          sortPackageName: p.packageByPackage.name,
        };
      }) || [],
    types: types || [],
    regions: regions || [],
    commodities: commodities || [],
  };

  return result;
};

export type TProductIdAndWidth = {
  gridId: string;
  productId?: string;
  width: number;
  columnFieldSelector?: string;
  columnSettings?: {
    decimalPlaces?: number | null;
    thousandsSeparator?: boolean | null;
  };
};

export function useGlobalProductOptions(data?: ReadonlyArray<TGlobalProduct>) {
  if (!data) return [];

  return R.pipe(
    data,
    R.groupBy((p) => p.globalPackageByGlobalPackage.name),
    R.mapValues(R.piped(R.sortBy([R.prop("name"), "asc"]))),
    R.values(),
    R.flat(),
    R.map((p) => ({
      label: globalProductName(p),
      value: p.id,
      disabled: p.globalPackageByGlobalPackage.global_permissions?.[0]?.permission === "list",
      groupValue: p.globalPackageByGlobalPackage.name,
      metadata: undefined,
      shortName: p.name,
      longName: p.description !== p.name ? p.description : undefined,
    })),
  );
}

/* ----- Curve Management Queries ----- */

export const useCurvePackages = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curvePackages", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurvePackageQuery);
      return readFragment(CurvePackageFragment, response.data?.package);
    },
  });
};

/**
 * Product/Linear "admin" for globalised-folder writes = UMI internal roles
 * (`umi-internal-admin` or `umi-internal-write`), not the org `manager` role.
 *
 * Check both sources: webapp JWTs often only list `user` in
 * `x-hasura-allowed-roles`, while the UMI roles live on `folio_user.roles`.
 */
export function useIsUmiInternalAdmin() {
  return useUmiRoleInfo().isUmiInternalAdmin;
}

/**
 * Hasura roles that may write the `global_*` mirror tables, in priority order.
 * Matches the override the admin app and the webapp's own API routes already use.
 */
const GLOBAL_WRITE_HASURA_ROLES = ["umi-internal-write", "umi-internal-admin"] as const;

/**
 * Everything the globalised-folder gating knows about the current user.
 *
 * `isUmiInternalAdmin` reads `folio_user.roles` only, which arrives over a
 * subscription and so tracks role changes live. The JWT is deliberately not
 * consulted: Auth0 caches the access token per origin and the app only forces a
 * fresh one near expiry, so a token that still lists a UMI role would keep
 * granting admin long after the role was taken away.
 *
 * `globalWriteHasuraRole` is the role to send for `global_*` writes, or null
 * when the token doesn't allow one. Only `x-hasura-allowed-roles` counts there:
 * roles on `folio_user.roles` are an app-level concept Hasura knows nothing
 * about, and asking for a role outside the allowed list gets rejected.
 */
export function useUmiRoleInfo() {
  const claims = useClaims();
  const { roles: folioRoles } = useCurrentUser();
  const hasFolioWrite = useUserHasRole("umi-internal-write");
  const hasFolioAdmin = useUserHasRole("umi-internal-admin");

  const allowedRoles = claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-allowed-roles"];
  const defaultRole = claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-default-role"];

  return useMemo(
    () => ({
      isUmiInternalAdmin: hasFolioWrite || hasFolioAdmin,
      globalWriteHasuraRole: GLOBAL_WRITE_HASURA_ROLES.find((role) => allowedRoles?.includes(role)) ?? null,
      jwtAllowedRoles: allowedRoles ? [...allowedRoles] : null,
      jwtDefaultRole: defaultRole ?? null,
      folioUserRoles: folioRoles ? [...folioRoles] : null,
    }),
    [hasFolioWrite, hasFolioAdmin, allowedRoles, defaultRole, folioRoles],
  );
}

function useGlobalWriteHasuraRole(): string | null {
  return useUmiRoleInfo().globalWriteHasuraRole;
}

const EMPTY_GLOBALISED_SET: ReadonlySet<number> = new Set<number>();

/**
 * Detection sources for "is this package globalised?".
 *
 * Non–UMI-admin UI gating uses `package.is_global` only (owners cannot see
 * their own rows in `global_package`, which is filtered by `global_permissions`).
 * Mirror sync may still consult `global_package` where the role can select it.
 */
type GlobalisedPackageSources = {
  /** Package ids from `global_package` (visible via global_permissions). */
  fromGlobalPackage: ReadonlySet<number>;
  /** Package ids from `package` where `is_global = true`. */
  fromIsGlobal: ReadonlySet<number>;
  /** Why a source is empty — a permission error reads the same as "none globalised". */
  errors: { globalPackage: string | null; isGlobal: string | null };
};

const EMPTY_GLOBALISED_SOURCES: GlobalisedPackageSources = {
  fromGlobalPackage: EMPTY_GLOBALISED_SET,
  fromIsGlobal: EMPTY_GLOBALISED_SET,
  errors: { globalPackage: null, isGlobal: null },
};

async function fetchGlobalisedPackageSources(
  graphql: ReturnType<typeof useGraphQLClient>,
): Promise<GlobalisedPackageSources> {
  const [mirrorsRes, flagsRes] = await Promise.all([
    graphql.execute(AllGlobalisedPackageIdsQuery),
    graphql.execute(GlobalisedPackageFlagsQuery),
  ]);

  const errors = {
    globalPackage: mirrorsRes.errors?.[0]?.message ?? null,
    isGlobal: flagsRes.errors?.[0]?.message ?? null,
  };
  if (errors.globalPackage) {
    logger.warn("Could not read global_package rows", { error: errors.globalPackage });
  }
  if (errors.isGlobal) {
    logger.warn("Could not read package.is_global flags", { error: errors.isGlobal });
  }

  const fromGlobalPackage = new Set<number>();
  const fromIsGlobal = new Set<number>();
  for (const pkg of mirrorsRes.data?.global_package ?? []) fromGlobalPackage.add(pkg.id as number);
  for (const pkg of flagsRes.data?.package ?? []) fromIsGlobal.add(pkg.id as number);

  return { fromGlobalPackage, fromIsGlobal, errors };
}

/**
 * Package ids treated as globalised for Curve Management UI gating
 * (grey-outs / blocks for non–UMI-admins, warnings for UMI admins).
 * Uses `package.is_global` only.
 */
export function useGlobalisedPackageIds(): ReadonlySet<number> {
  return useGlobalisedPackageSources().fromIsGlobal;
}

/** Both detection sources — useful for debugging stale `is_global` vs live mirrors. */
export function useGlobalisedPackageSources(): GlobalisedPackageSources {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  const query = useQuery({
    queryKey: ["globalisedPackageIds", userId],
    queryFn: () => fetchGlobalisedPackageSources(graphql),
    staleTime: 30_000,
  });

  return query.data ?? EMPTY_GLOBALISED_SOURCES;
}

export const useCurvePackageOptions = () => {
  const { data } = useCurvePackages();
  const userId = useUserId();
  const { username } = useCurrentUser();
  const isUmiInternalAdmin = useIsUmiInternalAdmin();
  const globalisedIds = useGlobalisedPackageIds();

  return useMemo(
    () =>
      R.pipe(
        data ?? [],
        R.map((pkg) => {
          // The current user's personal package is the User-type package they
          // own. We try the formal relation first (folio_users), then fall
          // back to a username match (some envs don't surface folio_users for
          // admin-level users, but the package name still matches the username).
          const folioMatch = (pkg.folio_users ?? []).some((u) => u.id === userId);
          const nameMatch = !!username && pkg.name.toLowerCase() === username.toLowerCase();
          const isCurrentUserPackage = pkg.package_type === "user" && (folioMatch || nameMatch);
          const isGlobalised = globalisedIds.has(pkg.id);

          return {
            label: pkg.name,
            value: pkg.id.toString(),
            groupValue: uppercaseFirstLetter(pkg.package_type || ""),
            permission: pkg.permissions?.[0]?.permission || null,
            isCurrentUserPackage,
            isGlobalised,
            // Non-UMI-admins cannot create/move into (or select) globalised folders.
            disabled: !isUmiInternalAdmin && isGlobalised,
            disabledTooltip: !isUmiInternalAdmin && isGlobalised ? GLOBALISED_NON_MANAGER_FOLDER_TOOLTIP : undefined,
          };
        }),
        // Group then label so all "Official" packages render under one
        // Official header, all "User" packages under one User header, etc.
        R.sortBy([R.prop("groupValue"), "asc"], [R.prop("label"), "asc"]),
      ),
    [data, userId, username, isUmiInternalAdmin, globalisedIds],
  );
};

export const useCurveUOMs = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curveUOMs", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveUOMQuery);
      return readFragment(CurveUOMFragment, response.data?.product_uom);
    },
  });
};

export const useCurveUOMOptions = () => {
  const { data } = useCurveUOMs();

  return useMemo(() => makeUomOptions(data ?? []), [data]);
};

export const useCurveProductTypes = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curveProductTypes", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveProductTypeQuery);
      return readFragment(CurveProductTypeFragment, response.data?.product_type);
    },
  });
};

export const useCurveProductTypeOptions = () => {
  const { data } = useCurveProductTypes();

  return useMemo(
    () => [
      {
        label: "None",
        value: "",
        groupValue: "",
      },
      ...R.pipe(
        data ?? [],
        R.map((type) => ({
          label: type.value.toUpperCase(),
          value: type.value,
          groupValue: "",
        })),
        R.sortBy([R.prop("label"), "asc"]),
      ),
    ],
    [data],
  );
};

export const useCurveCommodityParents = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curveCommodityParents", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveCommodityParentGroupsQuery);
      return readFragment(CurveCommodityParentGroupFragment, response.data?.commodity_parent_group);
    },
  });
};

export const useCurveCommodityComboboxOptions = () => {
  const { data: groups } = useCurveCommodities();
  const { data: parents } = useCurveCommodityParents();

  return useMemo(() => buildCurveCommodityComboboxOptions(groups, parents), [groups, parents]);
};

export const useCurveCommodityParentOptions = () => {
  const { data } = useCurveCommodityParents();

  return useMemo(
    () => [
      {
        label: "None",
        value: "",
        groupValue: "",
      },
      ...R.pipe(
        data ?? [],
        R.map((parent) => ({
          label: (parent.value || parent.id).toString(),
          value: parent.id.toString(),
          groupValue: "",
        })),
        R.sortBy([R.prop("label"), "asc"]),
      ),
    ],
    [data],
  );
};

export const useCurveRegions = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curveRegions", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveRegionsQuery);
      return readFragment(CurveRegionsFragment, response.data?.geographical_region);
    },
  });
};

export const useCurveRegionOptions = () => {
  const { data } = useCurveRegions();

  return useMemo(
    () => [
      {
        label: "None",
        value: "",
        groupValue: "",
      },
      ...R.pipe(
        data ?? [],
        R.map((region) => ({
          label: (region.value || region.id).toString().toUpperCase(),
          value: region.id.toString(),
          groupValue: "",
        })),
        R.sortBy([R.prop("label"), "asc"]),
      ),
    ],
    [data],
  );
};

export const useCurveCommodities = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["curveCommodities", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveCommodityGroupsQuery);
      return readFragment(CurveCommodityGroupFragment, response.data?.commodity_group);
    },
  });
};

// Based on the parent ID, return commodities that belong to that parent. If no parent is provided, return empty list.
export const useCurveCommodityOptions = (parent?: string) => {
  const { data } = useCurveCommodities();

  return useMemo(() => {
    if (!parent) {
      return [
        {
          label: "None",
          value: "",
          groupValue: "",
        },
      ];
    }

    return [
      {
        label: "None",
        value: "",
        groupValue: "",
      },
      ...R.pipe(
        data ?? [],
        R.filter((c) => c.parent?.toString() === parent),
        R.map((commodity) => ({
          label: (commodity.value || commodity.id).toString(),
          value: commodity.id.toString(),
          groupValue: "",
        })),
        R.sortBy([R.prop("label"), "asc"]),
      ),
    ];
  }, [data, parent]);
};

export const useEODCurves = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["eodCurves", userId],
    queryFn: async () => {
      const response = await graphql.execute(CurveEODProductsQuery, {
        folioUser: userId,
      });
      return readFragment(ProductInfoFragment, response.data?.product);
    },
  });
};

export const useEODCurveOptions = () => {
  const { data } = useEODCurves();
  return useMemo(() => makeEodCurveOptions(data ?? []), [data]);
};

export const useGlobalCurves = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    queryKey: ["globalCurves", userId],
    queryFn: async () => {
      const response = await graphql.execute(GlobalCurvesQuery, {
        folioUser: userId,
      });
      return readFragment(GlobalProductInfoFragment, response.data?.global_product);
    },
  });
};

export const useGlobalCurveOptions = () => {
  const { data } = useGlobalCurves();

  return useMemo(() => R.pipe(data ?? [], R.map(toGlobalCurveOption), R.sortBy([R.prop("label"), "asc"])), [data]);
};

export const useCurveGlobalDependencies = (productId: string | undefined) => {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["curveGlobalDependencies", productId],
    enabled: !!productId,
    queryFn: async () => {
      const response = await graphql.execute(CurveGlobalDependenciesQuery, {
        product: productId!,
      });
      return response.data?.product_global_dependencies as Array<{ product: string; global_product: string }>;
    },
  });
};

export const useCurveProductConfigById = (productId: string | undefined) => {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["curveProductConfigs", productId, CURVE_MANAGEMENT_MAX_CELLS],
    enabled: !!productId,
    queryFn: async () => {
      const response = await graphql.execute(ConfigsQuery, {
        maxRelMonth: CURVE_MANAGEMENT_MAX_CELLS,
      });
      return (response.data?.product_config ?? []).filter((config) => config.product === productId);
    },
  });
};

// Invalidates all caches that surface curve data so the UI shows the
// new/updated curve without a hard reload. The product info / configs
// queries are backed by an in-memory + IndexedDB cache that short-circuits
// when populated, so we have to clear it before invalidating React Query —
// otherwise the next refetch resolves from stale memory.
//
// This refresh is deliberately decoupled from the mutation's await chain
// (it's fired from `onSuccess` but never blocks it). The DB write has
// already succeeded by the time this runs, and both steps below can hang:
//   • `cache.clear()` is a raw IndexedDB op that can stall under contention.
//   • `invalidateQueries` resolves only once active queries refetch, and the
//     products query retries up to 20× with backoff — when the backend
//     rate-limits (429) that refetch can take minutes.
// Previously these were awaited inside the curve mutation's `onSuccess`, so a
// stuck refresh froze the Save dialog indefinitely and the user believed the
// save had failed even though the curve was persisted. See WEB-1789.
const CURVE_CACHE_CLEAR_TIMEOUT = 2_000;

function useInvalidateCurveCaches() {
  const qry = useQueryClient();
  const cache = useProductConfigsCache();
  return useCallback(async () => {
    // Bounded so a hanging IndexedDB clear can't stall the caller. The
    // in-memory side of the cache is reset synchronously inside clear(), so
    // the subsequent refetch still bypasses stale memory even on timeout.
    await timeout(CURVE_CACHE_CLEAR_TIMEOUT, cache.clear()).catch((err) =>
      logger.error("Failed to clear product cache after save", err),
    );

    // Fire-and-forget: never await the refetches (see note above).
    void Promise.all([
      qry.invalidateQueries({ queryKey: ["cached", "product-meta"] }),
      qry.invalidateQueries({ queryKey: ["products"] }),
      qry.invalidateQueries({ queryKey: ["configs"] }),
      qry.invalidateQueries({ queryKey: ["eodCurves"] }),
      qry.invalidateQueries({ queryKey: ["globalCurves"] }),
      qry.invalidateQueries({ queryKey: ["curveGlobalDependencies"] }),
      qry.invalidateQueries({ queryKey: ["curveProductConfigs"] }),
    ]).catch((err) => logger.error("Failed to refresh curve caches after save", err));
  }, [qry, cache]);
}

export const useCurveUpsertMutation = () => {
  const graphql = useGraphQLClient();
  const invalidate = useInvalidateCurveCaches();
  // The `product_id` variable scopes `delete_product_config` to the curve
  // being saved. Without it, a save that drops any offsets would delete
  // those `relative_month` rows from every other product the user can
  // write to in the org — e.g. shortening curve A wipes m17..m35 from any
  // other customer curve in the same org. See WEB-1708.
  type CurveUpsertVars = {
    products: Array<Product_Insert_Input>;
    product_configs: Array<Product_Config_Insert_Input>;
    offsets_to_delete: Array<number>;
    product_id: string;
  };
  return useMutation({
    mutationFn: ({
      products,
      configs,
      offsetsToDelete,
      productId,
    }: {
      products: Array<Product_Insert_Input>;
      configs: Array<Product_Config_Insert_Input>;
      offsetsToDelete: Array<number>;
      productId: string;
    }) => {
      logger.log("CurveUpsertMutation called with", { products, configs, offsetsToDelete, productId });
      return graphql.execute(CurveUpsertMutation as TypedDocumentNode<unknown, CurveUpsertVars>, {
        products,
        product_configs: configs,
        offsets_to_delete: offsetsToDelete,
        product_id: productId,
      });
    },
    onSuccess: () => invalidate(),
  });
};

export function useGlobalCurveInsertMutation() {
  const graphql = useGraphQLClient();
  const invalidate = useInvalidateCurveCaches();
  return useMutation({
    mutationFn: (globalDependencies: Array<{ product: string; global_product: string }>) => {
      return graphql.execute(CurveGlobalInsertMutation, {
        objects: globalDependencies,
      });
    },
    onSuccess: () => invalidate(),
  });
}

export function useGlobalCurveDeleteMutation() {
  const graphql = useGraphQLClient();
  const invalidate = useInvalidateCurveCaches();
  return useMutation({
    mutationFn: (params: { global_products: string[]; product: string }) => {
      return graphql.execute(CurveGlobalDeleteMutation, {
        global_products: params.global_products,
        product: params.product,
      });
    },
    onSuccess: () => invalidate(),
  });
}

export type SyncCurvesWithGlobalPackageArgs = {
  products: Array<Product_Insert_Input>;
  configs: Array<Product_Config_Insert_Input>;
  /** Config relative_months removed from the local product (also drop from global). */
  offsetsToDelete?: Array<number>;
  /** Package the curve(s) are being saved into. */
  targetPackageId: number;
};

/**
 * WEB-1953: keep `global_product` / `global_product_config` in sync when Curve
 * Management creates or moves curves in/out of a globalised package.
 *
 * Detection unions the live `global_package` table (WEB-1563) with the owning
 * side's `package.is_global` flag, since `global_package` is only selectable
 * for packages the role has global permissions on. When the destination is
 * not globalised we always attempt mirror cleanup (delete of missing rows is
 * a no-op), so orphaned mirrors from a previously interrupted move are still
 * removed on the next save.
 *
 * The mirror tables are writable by the UMI internal roles only, so the whole
 * sync runs under an `x-hasura-role` override and is skipped entirely for users
 * whose token doesn't allow one - they can't reach a globalised folder in the
 * UI, so they have no mirrors to create or clean up.
 */
export function useSyncCurvesWithGlobalPackage() {
  const baseGraphql = useGraphQLClient();
  const globalWriteRole = useGlobalWriteHasuraRole();
  const invalidate = useInvalidateCurveCaches();

  const graphql = useMemo(
    () => (globalWriteRole ? baseGraphql.withHeaders({ "x-hasura-role": globalWriteRole }) : null),
    [baseGraphql, globalWriteRole],
  );

  return useCallback(
    async ({ products, configs, offsetsToDelete = [], targetPackageId }: SyncCurvesWithGlobalPackageArgs) => {
      if (!graphql) return;
      const productIds = products.map((p) => p.id).filter((id): id is string => !!id);
      if (productIds.length === 0 || targetPackageId == null || Number.isNaN(targetPackageId)) return;

      const [globalPackagesRes, globalFlagsRes, mirrorsRes] = await Promise.all([
        graphql.execute(GlobalPackagesByIdsQuery, { ids: [targetPackageId] }),
        graphql.execute(GlobalisedPackageFlagsQuery),
        graphql.execute(GlobalCurveMirrorsQuery, { ids: productIds }),
      ]);
      if (globalPackagesRes.errors?.length || globalFlagsRes.errors?.length) {
        throw new Error(
          globalPackagesRes.errors?.[0]?.message ??
            globalFlagsRes.errors?.[0]?.message ??
            "Failed to look up global packages",
        );
      }
      if (mirrorsRes.errors?.length) {
        throw new Error(mirrorsRes.errors[0]?.message ?? "Failed to look up global curve mirrors");
      }

      const targetIsGlobal =
        (globalPackagesRes.data?.global_package ?? []).some((pkg) => pkg.id === targetPackageId) ||
        (globalFlagsRes.data?.package ?? []).some((pkg) => pkg.id === targetPackageId);
      // Empty can mean "no mirror" OR "role cannot select global_product".
      const mirrors = mirrorsRes.data?.global_product ?? [];

      // Destination is not globalised: always attempt mirror cleanup. We don't
      // gate cleanup on the select above — webapp roles may lack select but
      // still be allowed to delete (and delete of missing rows is a no-op).
      if (!targetIsGlobal) {
        logger.log("Removing global curve mirrors (curve not in a globalised package)", {
          productIds,
          targetPackageId,
          foundMirrors: mirrors.length,
        });
        const removeRes = await graphql.execute(RemoveGlobalCurvesMutation, {
          globalProductIds: productIds,
        });
        if (removeRes.errors?.length) {
          throw new Error(removeRes.errors[0]?.message ?? "Failed to remove global curve mirrors");
        }
        void invalidate();
        return;
      }

      const globalProducts: Array<Global_Product_Insert_Input> = products
        .filter((p): p is Product_Insert_Input & { id: string } => !!p.id)
        .map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          uom: p.uom,
          geographical_region: p.geographical_region ?? undefined,
          commodity_group: p.commodity_group ?? undefined,
          curve_type: p.curve_type,
          global_package: targetPackageId,
        }));

      const globalProductConfigs: Array<Global_Product_Config_Insert_Input> = configs
        .filter(
          (c): c is Product_Config_Insert_Input & { product: string; relative_month: number } =>
            !!c.product && c.relative_month != null,
        )
        .map((c) => ({
          product: c.product,
          relative_month: c.relative_month,
          formula: c.formula,
        }));

      logger.log("Upserting global curve mirrors for globalised package", {
        productIds,
        targetPackageId,
        configCount: globalProductConfigs.length,
        offsetsToDelete,
      });

      const upsertRes = await graphql.execute(UpsertGlobalCurvesMutation, {
        globalProducts,
        globalProductConfigs,
        offsetsToDelete,
        productIds,
      });
      if (upsertRes.errors?.length) {
        throw new Error(upsertRes.errors[0]?.message ?? "Failed to upsert global curve mirrors");
      }

      const selfDeps = productIds.map((id) => ({ product: id, global_product: id }));
      const depsRes = await graphql.execute(InsertGlobalCurveSelfDepsMutation, { objects: selfDeps });
      if (depsRes.errors?.length) {
        throw new Error(depsRes.errors[0]?.message ?? "Failed to insert global curve self-dependencies");
      }

      void invalidate();
    },
    [graphql, invalidate],
  );
}

export const curveTypes = [
  { label: "Live", value: "live", groupValue: "" },
  { label: "Snapshot", value: "snapshot", groupValue: "" },
  { label: "Delta", value: "delta", groupValue: "" },
];

export const periodAggregations = [
  { label: "None", value: "none", groupValue: "" },
  { label: "Average", value: "average", groupValue: "" },
  { label: "Hourly Average", value: "hourly_weighted_average", groupValue: "" },
  { label: "Sum", value: "sum", groupValue: "" },
  { label: "Time Spread", value: "timespread", groupValue: "" },
  { label: "Hourly Time Spread", value: "hourly_weighted_timespread", groupValue: "" },
];

export function useScroll({
  containerRef,
  setInputTop,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  setInputTop: React.Dispatch<React.SetStateAction<number>>;
}) {
  useEffect(() => {
    const container = document.getElementById("curve-management-container");
    if (!container) return;

    function handleScroll() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setInputTop(rect.bottom + window.scrollY - rect.height);
      }
    }

    handleScroll(); // Initial call to set position

    container.addEventListener("scroll", handleScroll);
    container.addEventListener("resize", handleScroll);
    container.addEventListener("click", handleScroll);
    container.addEventListener("keyup", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("resize", handleScroll);
      container.removeEventListener("click", handleScroll);
      container.removeEventListener("keyup", handleScroll);
    };
  }, [containerRef, setInputTop]);
}

// Extract product IDs from configs' formulas by getting the items inside the square brackets and deduping them.
export function useProductsInConfigs(configs: Array<{ offset: number; formula?: string | null | undefined }>) {
  const productIds = useMemo(() => {
    const productIds = configs.flatMap((config) => {
      if (!config.formula) return [];

      const regex = /\[([^\]]+)\]/g;
      const matches = config.formula.matchAll(regex);
      const ids = Array.from(matches, (m) => m[1].trim());
      return ids;
    });

    return Array.from(new Set(productIds));
  }, [configs]);

  return productIds;
}

// Converts configs to entries for the formula form.
export function useEntriesFromConfigs(
  configs: Array<{ offset: number; formula?: string | null | undefined }>,
  products: TCurve[] | undefined,
  selectors: TSelectors | undefined,
): {
  entries: TEntry[];
  productIds: string[];
} {
  const productIds = useProductsInConfigs(configs);
  return useMemo(() => {
    if (!configs || configs.length === 0) return { entries: [], productIds };

    // Only wait while the products list is still loading (undefined). An empty
    // array means products loaded but none resolved (e.g. deleted/inaccessible
    // input curves) — in that case we still build entries from the formula
    // strings so the broken-on-load UI can surface them rather than collapsing
    // into a single empty default segment.
    if (productIds.length > 0 && products === undefined) {
      return { entries: [], productIds };
    }

    // First, sort by offset.
    const sortedConfigs = configs.sort((a, b) => a.offset - b.offset);

    // Then, we loop through, and break them up into entries with repetitions based on whether the next config has the same formula. Once the formula changes, we create a new entry.
    const entries: TEntry[] = [];
    let currentEntry: TEntry | null = null;
    let currentEntryStartOffset = 0;
    let prevBlockEndOffset = -1;

    sortedConfigs.forEach((config, index) => {
      if (!currentEntry) {
        const rawFormula = replaceFormulaIds(config.formula || "", products || []);
        const items = parseFormula(products || [], selectors || [], rawFormula);
        currentEntryStartOffset = config.offset;
        currentEntry = {
          id: nanoid(),
          formula: parseItems(items),
          items,
          repetitions: 1,
          manualEntry: config.formula === null,
          // Preserve any gap between this block and the previous block (or
          // the start of the curve) so the form labels and round-trip save
          // reflect the original offsets rather than a contiguous re-numbering.
          gapBefore: currentEntryStartOffset - (prevBlockEndOffset + 1),
        };
      }

      const nextConfig = sortedConfigs[index + 1];
      const isAdjacent = nextConfig && nextConfig.offset === config.offset + 1;
      if (nextConfig && nextConfig.formula === config.formula && isAdjacent) {
        // Same formula and contiguous offset — extend the block.
        currentEntry.repetitions += 1;
      } else {
        // Different formula or a gap in offsets — finalise this block so
        // gaps render as separate manual-entry blocks instead of one merged block.
        entries.push(currentEntry);
        prevBlockEndOffset = config.offset;
        currentEntry = null;
      }
    });

    return {
      entries,
      productIds,
    };
  }, [configs, products, selectors, productIds]);
}

export function useExistingCurve(selectedProduct: string | undefined) {
  const {
    curveId,
    curvePackage,
    setCurveId,
    setCurvePackage,
    setCurveName,
    setDisplayName,
    setCurveType,
    setUom,
    setPeriodAggregation,
    setProductType,
    setParent,
    setRegion,
    setCommodity,
    setEod,
    setFormulas,
    setPendingInputCurves,
    setConfigs,
    setGlobal,
    setPendingGlobalCurves,
    setCopiedFromPackage,
    setFormSnapshot,
    setUseCurveNameAsDisplay,
  } = useCurveFormContext();

  const productId = selectedProduct ?? curveId ?? "";
  const productQuery = useProductById(productId);
  const product = productQuery.data;
  const configsQuery = useCurveProductConfigById(productId);
  const configs = configsQuery.data;

  const inputCurves = useProductsInConfigs(configs ?? []);
  const productsInEntriesQuery = useProductsByIds(inputCurves);
  const productsInEntries = productsInEntriesQuery.data;
  const selectors = useMemo(() => {
    return getInputCurveSelectors(productsInEntries);
  }, [productsInEntries]);

  const entries = useEntriesFromConfigs(configs ?? [], productsInEntries, selectors);
  const productOptions = useProductOptions({ products: productsInEntries || [] });
  const globalDependencies = useCurveGlobalDependencies(productId).data || [];
  // Self-deps (product → its own global_product) are managed by the
  // globalisation sync (WEB-1953), not the "Third party feeds" picker.
  const thirdPartyGlobalDeps = useMemo(
    () => globalDependencies.filter((dep) => dep.global_product !== productId),
    [globalDependencies, productId],
  );
  const globalOptions = useGlobalCurveOptions().filter((option) =>
    thirdPartyGlobalDeps.some((dep) => dep.global_product === option.value),
  );

  const applyConfigs = useCallback(() => {
    setConfigs(configs);
  }, [configs, setConfigs]);

  const applyEntries = useCallback(() => {
    if (entries.entries.length === 0) {
      setFormulas([
        {
          id: nanoid(),
          formula: "",
          items: [],
          repetitions: 36,
          manualEntry: false,
        },
      ]);
      return;
    }

    setFormulas(entries.entries);
  }, [entries.entries, setFormulas]);

  const applyDetails = useCallback(
    (edit: boolean) => {
      if (product) {
        if (edit) {
          setCurveId(product.id);
          setCurvePackage(product.package?.toString());
          setCopiedFromPackage(undefined);
        } else {
          setCurveId(uuidv4());
          setCopiedFromPackage(product.package?.toString());
        }

        setCurveName(product.description || "");
        setDisplayName(product.name || "");
        setUseCurveNameAsDisplay(false);
        setCurveType(product.curve_type);
        setUom(product.uom);
        setPeriodAggregation(product.summary_type || "");
        setProductType(product.product_type || "");
        setParent(product.commodityGroupByCommodityGroup?.parent.toString() || "");
        setRegion(product.geographical_region?.toString() || "");
        setCommodity(product.commodity_group?.toString() || "");
        setEod(product.eod_product_dep || "");
        setPendingInputCurves?.(productOptions.curves);
        setGlobal(thirdPartyGlobalDeps.map((d) => d.global_product));
        setPendingGlobalCurves?.(globalOptions);
      }
    },
    [
      product,
      setCurvePackage,
      setCurveId,
      setCurveName,
      setCurveType,
      setUom,
      setPeriodAggregation,
      setProductType,
      setParent,
      setRegion,
      setCommodity,
      setEod,
      setPendingInputCurves,
      setDisplayName,
      setUseCurveNameAsDisplay,
      productOptions.curves,
      thirdPartyGlobalDeps,
      setGlobal,
      setPendingGlobalCurves,
      globalOptions,
      setCopiedFromPackage,
    ],
  );

  const apply = useCallback(
    (edit: boolean) => {
      applyDetails(edit);
      applyConfigs();
      applyEntries();

      if (product) {
        // Capture a baseline of the just-applied form state so the
        // "Reset changes" affordance can detect divergence and revert
        // back to it. For Edit, the package matches the source curve;
        // for Copy, the package is whatever the user already had set
        // (apply doesn't overwrite it in copy mode).
        const baselineFormulas: TEntry[] =
          entries.entries.length === 0
            ? [
                {
                  id: nanoid(),
                  formula: "",
                  items: [],
                  repetitions: 36,
                  manualEntry: false,
                },
              ]
            : entries.entries;

        setFormSnapshot({
          mode: edit ? "edit" : "copy",
          curvePackage: edit ? (product.package?.toString() ?? "") : (curvePackage ?? ""),
          curveName: product.description ?? "",
          displayName: product.name ?? "",
          curveType: product.curve_type ?? "",
          uom: product.uom ?? "",
          periodAggregation: product.summary_type ?? "",
          productType: product.product_type ?? "",
          parent: product.commodityGroupByCommodityGroup?.parent.toString() ?? "",
          region: product.geographical_region?.toString() ?? "",
          commodity: product.commodity_group?.toString() ?? "",
          eod: product.eod_product_dep ?? "",
          global: globalOptions.map((o) => o.value),
          formulas: baselineFormulas,
          pendingInputCurves: productOptions.curves,
          pendingGlobalCurves: globalOptions,
        });
      }
    },
    [
      applyConfigs,
      applyDetails,
      applyEntries,
      product,
      entries.entries,
      productOptions.curves,
      globalOptions,
      curvePackage,
      setFormSnapshot,
    ],
  );

  const loaded =
    Boolean(product) && configsQuery.isSuccess && (inputCurves.length === 0 || productsInEntriesQuery.isSuccess);
  const loadFailed = productQuery.isError || configsQuery.isError || productsInEntriesQuery.isError;

  return { apply, loaded, loadFailed, productResolved: productQuery.isSuccess, productFound: Boolean(product) };
}

/**
 * Per-entry "reset to applied baseline" helper. Powers the inline undo
 * icon on each formula segment: when the live entry diverges from the
 * snapshot captured at apply time (Edit or Copy), the icon enables and
 * `resetEntry` reverts just that one block. Returns `getBaseline`/
 * `hasEntryChanges`/`resetEntry` keyed by entry id so callers can drive
 * a single button per block without re-running the whole hook.
 */
export function useEntryBaseline() {
  const { formSnapshot, formulas, setFormulas, setFormSnapshot } = useCurveFormContext();

  const baselineById = useMemo(() => {
    const map = new Map<string, TEntry>();
    if (formSnapshot?.mode !== "edit") return map;
    for (const e of formSnapshot?.formulas ?? []) map.set(e.id, e);
    return map;
  }, [formSnapshot]);

  const getBaseline = useCallback((entryId: string) => baselineById.get(entryId), [baselineById]);

  const hasEntryChanges = useCallback(
    (entryId: string) => {
      const baseline = baselineById.get(entryId);
      if (!baseline) return false;
      const current = formulas.find((e) => e.id === entryId);
      if (!current) return false;
      // Only flag changes to the formula itself (string + parsed items).
      // Repetitions, manualEntry and the derived `valid` flag are ignored —
      // the reset icon is purely for reverting formula edits.
      return !formulaContentEqual(baseline, current);
    },
    [baselineById, formulas],
  );

  /** When false, delete this segment immediately (edit mode, block still matches snapshot). */
  const segmentDeleteNeedsConfirmation = useCallback(
    (entryId: string) => {
      if (formSnapshot?.mode !== "edit") return true;
      const baseline = baselineById.get(entryId);
      if (!baseline) return true;
      const current = formulas.find((e) => e.id === entryId);
      if (!current) return true;
      if (!formulaContentEqual(baseline, current)) return true;
      if (baseline.repetitions !== current.repetitions) return true;
      if (baseline.manualEntry !== current.manualEntry) return true;
      return false;
    },
    [formSnapshot?.mode, baselineById, formulas],
  );

  const resetEntry = useCallback(
    (entryId: string) => {
      const baseline = baselineById.get(entryId);
      if (!baseline) return;
      // <Entry> initializes its internal `items` state from `entry.items`
      // only on mount, so swapping the entry's content via `setFormulas`
      // alone is overwritten by the next emit from <Entry>. We therefore
      // also mint a fresh entry id: <FormulaForm> uses `key={entry.id}`,
      // so the new id forces a remount and the component re-initializes
      // from the baseline content. Items get fresh itemIds so they don't
      // alias the snapshot's array. Repetitions and manualEntry are
      // preserved — the reset is scoped to formula content only.
      const newId = nanoid();
      const freshItems = baseline.items.map((item) => ({ ...item, itemId: nanoid() }));

      setFormulas((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                id: newId,
                formula: baseline.formula,
                items: freshItems,
                valid: baseline.valid,
              }
            : e,
        ),
      );

      // Mirror the new id in the snapshot so the same logical block keeps
      // matching its baseline on subsequent change-detection passes.
      setFormSnapshot((prev) =>
        prev
          ? {
              ...prev,
              formulas: prev.formulas.map((f) => (f.id === entryId ? { ...f, id: newId } : f)),
            }
          : prev,
      );
    },
    [baselineById, setFormulas, setFormSnapshot],
  );

  return { getBaseline, hasEntryChanges, resetEntry, segmentDeleteNeedsConfirmation };
}

// Compares the raw formula strings of two entries after running both
// through `formatFormulaOperators` so cosmetic differences (whitespace
// around operators, padding next to parens) don't register as changes.
// `replaceFormulaIds` and `parseItems` produce slightly different
// whitespace, but their normalized forms agree.
function formulaContentEqual(a: TEntry, b: TEntry): boolean {
  return formatFormulaOperators(a.formula ?? "") === formatFormulaOperators(b.formula ?? "");
}

/**
 * Shared rules for “you might lose work”: switching Create/Edit or leaving the page.
 * Create: any curve name, display name, or segment. Edit: form differs from snapshot.
 */
export function useCurveFormDiscardGuards() {
  const {
    curveName,
    displayName,
    formulas,
    editingCurve,
    formSnapshot,
    curveType,
    uom,
    periodAggregation,
    productType,
    region,
    commodity,
    eod,
    curvePackage,
    global,
  } = useCurveFormContext();

  const formHasContent = !!(curveName || displayName || formulas.length > 0);

  const editFormHasChanges = useMemo(() => {
    if (!editingCurve || !formSnapshot) return false;
    const s = formSnapshot;
    if (s.curveName !== curveName) return true;
    if (s.displayName !== displayName) return true;
    if (s.curveType !== curveType) return true;
    if (s.uom !== uom) return true;
    if (s.periodAggregation !== periodAggregation) return true;
    if (s.productType !== (productType ?? "")) return true;
    if (s.region !== (region ?? "")) return true;
    if (s.commodity !== (commodity ?? "")) return true;
    if (s.eod !== (eod ?? "")) return true;
    if (s.curvePackage !== (curvePackage ?? "")) return true;
    if (JSON.stringify([...(s.global ?? [])].sort()) !== JSON.stringify([...(global ?? [])].sort())) return true;
    if (s.formulas.length !== formulas.length) return true;
    const normalizeFormula = (f: TEntry) => ({
      formula: formatFormulaOperators(f.formula ?? ""),
      repetitions: f.repetitions,
      manualEntry: f.manualEntry,
      gapBefore: f.gapBefore ?? 0,
    });
    if (JSON.stringify(s.formulas.map(normalizeFormula)) !== JSON.stringify(formulas.map(normalizeFormula)))
      return true;
    return false;
  }, [
    editingCurve,
    formSnapshot,
    curveName,
    displayName,
    curveType,
    uom,
    periodAggregation,
    productType,
    region,
    commodity,
    eod,
    curvePackage,
    global,
    formulas,
  ]);

  return { formHasContent, editFormHasChanges };
}

export function useCurveNameExists(
  curvePackage: string,
  curveName: string,
  _editingCurve: boolean,
  currentCurveId?: string,
) {
  const { data: productOptionsData } = useAllProductsOptions();

  return useMemo(() => {
    const pkg = curvePackage?.trim();
    const name = curveName?.trim();
    if (!pkg || !name) return false;

    return Boolean(
      productOptionsData.curves?.some((curve) => {
        if (curve.metadata.package.toString() !== pkg) return false;
        if (curve.metadata.description?.trim() !== name) return false;
        // A curve never collides with itself. Excluding by id in both create
        // and edit modes prevents a transient "duplicate" flash when the
        // products query refetches after a successful create — at that point
        // the just-saved curve briefly appears as its own duplicate before
        // the Save modal closes.
        if (currentCurveId && curve.metadata.id === currentCurveId) return false;
        return true;
      }),
    );
  }, [productOptionsData.curves, curvePackage, curveName, currentCurveId]);
}

/**
 * Returns true if the current org already contains a delta curve linked to the
 * curve being edited. A delta curve is identified by its name matching the
 * `Δ<displayName>` regex (the pattern produced by the "Create Delta Curve"
 * flow). Used to grey out the "Create Delta Curve" checkbox during edit.
 */
export function useDeltaCurveExists(displayName: string, editingCurve: boolean) {
  const { data: productOptionsData } = useAllProductsOptions();
  return useMemo(() => {
    if (!editingCurve) return false;
    const trimmed = displayName?.trim();
    if (!trimmed) return false;
    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^Δ${escaped}$`);
    return Boolean(productOptionsData.curves?.some((c) => c.metadata.name && re.test(c.metadata.name)));
  }, [productOptionsData.curves, displayName, editingCurve]);
}

export function useProductSelectionDisabled(selectedProduct: string | undefined) {
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    if (selectedProduct) {
      setDisabled(true);
      const timeout = setTimeout(() => {
        setDisabled(false);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setDisabled(false);
    }
  }, [selectedProduct]);
  return disabled;
}

/** Scroll the segments list so a newly added or duplicated block is visible. */
export function scrollCurveSegmentIntoView(segmentId: string, options?: { revealAddActions?: boolean }) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const scrollArea = document.getElementById("segments-scroll-area");
      if (!scrollArea) return;

      if (options?.revealAddActions) {
        const addActions = scrollArea.querySelector<HTMLElement>("[data-segment-add-actions]");
        addActions?.scrollIntoView({ block: "end", behavior: "smooth" });
        return;
      }

      const segment = scrollArea.querySelector<HTMLElement>(`[data-segment-id="${segmentId}"]`);
      segment?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  });
}
